/**
 * betFreeze display maths, shared by every console (classic MyBets, American
 * BetDetailModal / BetItem). Display only: prices come from rocky-bets, the
 * client never computes a price it sends back.
 */

const isMultipleType = (bet) => Boolean(bet) && (bet.type === 'multiple' || bet.type === 'single');

const hasWinningTotal = (detail) => Boolean(detail) && detail.winning_total != null;

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

// Potential figures rocky-definition recalculated on a pending bet whose legs
// no longer match the placement (void, cancelled, half, frozen leg):
// potential_detail, or betFreeze_detail on bets frozen before it existed.
const pendingDetail = (bet) => {
  if (hasWinningTotal(bet.potential_detail)) return bet.potential_detail;
  if (bet.betFreeze && hasWinningTotal(bet.betFreeze_detail)) return bet.betFreeze_detail;
  return null;
};

// Potential winnings of a pending single/multiple. After a void / half leg
// or a betFreeze the placement-time figure is obsolete: rocky-definition
// stores the new one.
export const pendingPotential = (bet) => {
  const detail = pendingDetail(bet);
  if (detail) return parseFloat(detail.winning_total);
  return bet._multiple_detail ? parseFloat(bet._multiple_detail.winning_total) : NaN;
};

// Figures that replace the placement ones: the recalculated potential while
// pending, the settlement recalculation (void_detail) once won. Null when
// the placement figures still hold.
export const recalculatedDetail = (bet) => {
  if (!isMultipleType(bet)) return null;
  if (bet.result === 'pending') return pendingDetail(bet);
  if (bet.result === 'win' && hasWinningTotal(bet.void_detail)) return bet.void_detail;
  return null;
};

// Multiple bonus of a single/multiple: the one that applies today (value and
// percentage) and, when it changed, the placement one to strike through.
// A void or frozen leg leaves the bonus tier, so the percentage can drop.
// Null for integral / system bets.
export const bonusFigures = (bet) => {
  if (!isMultipleType(bet) || !bet._multiple_detail) return null;
  const placed = {bonus: toNumber(bet._multiple_detail.bonus), percentage: toNumber(bet._multiple_detail.bonus_percentage)};
  const detail = recalculatedDetail(bet);
  if (!detail) return {current: placed, original: null};
  const current = {bonus: toNumber(detail.bonus), percentage: toNumber(detail.bonus_percentage)};
  const changed = current.bonus.toFixed(2) !== placed.bonus.toFixed(2) || current.percentage !== placed.percentage;
  return {current, original: changed ? placed : null};
};

// Placement-time potential, shown struck through next to the current one.
// Null when nothing was recalculated (no freeze, no void / half leg) or the
// figure did not change.
export const originalPotential = (bet) => {
  if (!isMultipleType(bet) || (!bet.betFreeze && !hasWinningTotal(bet.potential_detail))) return null;
  const original = bet.betFreeze_detail && bet.betFreeze_detail.original && bet.betFreeze_detail.original.winning_total != null
    ? parseFloat(bet.betFreeze_detail.original.winning_total)
    : parseFloat(bet._multiple_detail && bet._multiple_detail.winning_total);
  if (!Number.isFinite(original) || original.toFixed(2) === pendingPotential(bet).toFixed(2)) return null;
  return original;
};

// A frozen leg the operator voided afterwards with the whole bet (admin void,
// agency cancellation): it no longer pays the frozen odd. rocky-definition
// marks it in betFreeze.overridden; a void/cancelled frozen leg without the
// mark (voided before the mark existed) counts too.
export const isFreezeOverridden = (odd) => Boolean(odd && odd.betFreeze
  && (odd.betFreeze.overridden || odd.void === true || odd.cancelled === true));

// A frozen leg still paying its frozen odd.
export const isFreezeActive = (odd) => Boolean(odd && odd.betFreeze && !isFreezeOverridden(odd));

// Fair value of the leg today = placed / current odd (what freezing would be
// worth with no margin), and the share of it the frozen odd gives back.
export const fairInfo = (legState) => {
  if (!legState) return null;
  const initial = parseFloat(legState.initialOdd);
  const current = parseFloat(legState.currentOdd);
  const frozen = parseFloat(legState.frozenOdd);
  if (!(initial > 0) || !(current > 0) || !(frozen > 0)) return null;
  const fair = initial / current;
  const kept = Math.min(100, Math.round((frozen / fair) * 100));
  return {fair: Math.floor(fair * 100 + 1e-9) / 100, kept, margin: 100 - kept};
};

// What the freeze costs: current potential minus the new one, in money and
// as a share of the current potential. The new one is the server estimate,
// or current × frozen / placed when the quote carries none (same formula).
// Used by the leg bar AND the confirm panel, so the two always match.
export const freezeCost = (bet, legState) => {
  if (!legState || !bet || !bet._multiple_detail) return null;
  const current = pendingPotential(bet);
  const initial = parseFloat(legState.initialOdd);
  const frozen = parseFloat(legState.frozenOdd);
  const next = legState.estimatedPotentialWinning != null
    ? parseFloat(legState.estimatedPotentialWinning)
    : (initial > 0 && Number.isFinite(frozen) ? current * frozen / initial : NaN);
  if (!Number.isFinite(current) || !Number.isFinite(next) || current <= 0 || next >= current) return null;
  return {amount: (current - next).toFixed(2), percent: Math.round((1 - next / current) * 100)};
};
